import axios, { AxiosError, AxiosInstance } from 'axios';

import { config } from './config';

/**
 * Single configured Axios instance. BUILD_STANDARDS §1.5:
 *   - No ad-hoc axios.get() in components.
 *   - Attach auth cookie + request id via interceptors.
 *   - Global 401 handler routes to /login instead of looping failed requests.
 *
 * All resource-specific request functions in `src/api/*` import THIS
 * instance rather than the axios module directly.
 */
class ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;

  constructor(message: string, status: number, code: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function buildClient(): AxiosInstance {
  const client = axios.create({
    // Empty baseURL so relative paths (`/api/v1/…`) go through Vite's dev
    // proxy in dev and through nginx in prod. Keeps the app portable
    // across environments without a config swap.
    baseURL: '',
    withCredentials: true, // send + receive httpOnly session cookie
    timeout: 30_000,
    headers: { 'X-Requested-With': 'MailingBeesDashboard' },
  });

  client.interceptors.response.use(
    (r) => r,
    (err: AxiosError<{ error?: { code?: string; message?: string; details?: Record<string, unknown> } }>) => {
      const status = err.response?.status ?? 0;
      const envelope = err.response?.data?.error;
      const code = envelope?.code ?? `HTTP_${status || 'NETWORK'}`;
      const message = envelope?.message ?? err.message ?? 'Request failed';
      // 401 outside the /auth flow → force operator to log in again.
      if (status === 401 && !err.config?.url?.includes('/auth/')) {
        window.dispatchEvent(new CustomEvent('mb:unauthorized'));
      }
      return Promise.reject(new ApiError(message, status, code, envelope?.details));
    },
  );

  return client;
}

const axiosInstance = buildClient();

export const apiClient = {
  get: async <T>(url: string, params?: Record<string, unknown>): Promise<T> => {
    const r = await axiosInstance.get<T>(url, { params });
    return r.data;
  },
  post: async <T>(url: string, body?: unknown): Promise<T> => {
    const r = await axiosInstance.post<T>(url, body);
    return r.data;
  },
  patch: async <T>(url: string, body?: unknown): Promise<T> => {
    const r = await axiosInstance.patch<T>(url, body);
    return r.data;
  },
  put: async <T>(url: string, body?: unknown): Promise<T> => {
    const r = await axiosInstance.put<T>(url, body);
    return r.data;
  },
  del: async <T>(url: string): Promise<T> => {
    const r = await axiosInstance.delete<T>(url);
    return r.data;
  },
};

export { ApiError };
export type PageEnvelope<T> = { items: T[]; next_cursor: string | null; limit: number };

// Re-export for tests that want to introspect the base URL without importing config.
export const API_BASE = config.apiBaseUrl;
